import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, first, takeUntil, Subject, firstValueFrom, map } from 'rxjs';
import { IConsumer } from 'src/models/consumer';
import { ConsumerLogin } from 'src/models/consumer-login';
import { CustomerInformation } from 'src/models/customer';
import { AlertService } from 'src/services/alert.service';
import { ApiService } from 'src/services/api.service';
import { CustomerStateService } from 'src/services/customer-state.service';
import { jwtDecode } from "jwt-decode";
import { PageLoadingService } from 'src/services/page-loading.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  modalRef?: BsModalRef;
  phoneNumber!: FormGroup;
  loginForm!: FormGroup;
  loginNumber: string = '';
  rawLoginNumber: string;
  signupNumber: string = '';
  rawSignupNumber: string;
  submitted = false;

  private readonly onDestroy$ = new Subject<void>();
  private readonly specialKeys = new Array<string>('Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter');
  public customers: CustomerInformation[] = null;
  public consumerLogin: ConsumerLogin;
  public consumer: IConsumer;
  public customerId$: Observable<string>;
  
  public backgroundImage: string = 'url("../../assets/Splash_Background.jpg")';

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private apiService: ApiService,
    private alertService: AlertService,
    public customerStateService: CustomerStateService,
    private loadingService: PageLoadingService
  ) { 
    document.body.style.overflow = 'hidden'
  }
  
  ngOnInit() {
    if (this.router.url.includes('/customer/',0)){
      this.customerStateService.customerId = this.router.url.substring(10,this.router.url.lastIndexOf('/'))
    }
    this.loginForm = this.formBuilder.group({
      customerId: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.pattern('[- +()0-9]{10,12}')]],
      password: ['', Validators.required]
    });
    this.phoneNumber = this.formBuilder.group({
        phoneNumber: ['', [Validators.required, Validators.pattern('[- +()0-9]{10,12}')]],
    });

    this.customerStateService.currentCustomerInfo$.pipe(takeUntil(this.onDestroy$)).subscribe(
      customerInfo =>  {
        this.backgroundImage = customerInfo ? "url('data:image/svg;base64," + customerInfo.backgroundImage : 'url("../../assets/Splash_Background.jpg")'
      })
  }

  get numberValue() { return this.phoneNumber.controls; }
  get loginValue() { return this.loginForm.controls; }

  async onLogin() {
    this.loadingService.pageLoaded = false;
    this.apiService.login(this.loginValue.phoneNumber.value, this.loginValue.password.value, this.customerStateService.customerId)
      .pipe(first()).subscribe({ 
      next: async consumer => {
        this.loginForm.setErrors({ unauthenticated: false });
        this.consumerLogin = consumer;
        let decoded = jwtDecode(consumer.token);
        let expiration = decoded.exp*1000;
        document.cookie = `expires=${expiration};`;
        document.cookie = `token=${consumer.token};`;
        //== For testing endpoint response ==// 
          // console.log("Login response:", consumer);
          // console.log("My token:", jwtDecode(consumer.token), "expires", new Date(expiration) );
        this.customerStateService.consumerDetails = await this.apiService.getConsumerDetailsAsync(consumer.consumer.customerId, this.loginValue.phoneNumber.value);
        this.customerStateService.customerDetails = await this.apiService.getCustomerDetailsAsync(consumer.consumer.customerId, 'true');
        
        this.router.navigate([`/customer/${this.customerStateService.customerId}/manage-my-membership/`]);
      },
      error: error => {
        this.loadingService.pageLoaded = true;
        this.loginForm.setErrors({ unauthenticated: true });
        this.loginForm.controls['password'].setValue('');
        this.alertService.error(error.error[""]);
      }
    })
    
  }

  onSignup(){
    this.loadingService.pageLoaded = false;
    // this.apiService.signUp(this.numberValue.phoneNumber.value, this.customerStateService.customerId)
    this.apiService.signUp(this.rawSignupNumber, this.customerStateService.customerId)
      .pipe(first()).subscribe({ 
      next: consumer => {
        this.loginForm.setErrors({ unauthenticated: false });
        
        this.consumer = consumer;
        alert("This phone number already exists: " + consumer.mobileNumber);
      },
      error: error => {
        this.loginForm.setErrors({ unauthenticated: true });
        this.alertService.error(error);
        this.customerStateService.mobileNumber = this.numberValue.phoneNumber.value
        this.router.navigate([`/customer/${this.customerStateService.customerId}/signup`])
      }})
      this.loadingService.pageLoaded = true;
  }

  async toResetPwdPage(){
    this.router.navigate([`/customer/${this.customerStateService.customerId}/reset-password`])
  }

  checkKey(ev: KeyboardEvent, number){
    const regex = /^[0-9]+$/;
    if(!this.specialKeys.includes(ev.key)){
        if(number.length >= 14 || !regex.test(ev.key)){
            ev.preventDefault();
        }
    }
}

formatPhone(isLoginNumber: boolean){
  let num = new Array<string>(14);
    num[0] = '(';
    num[4] = ')';
    num[5] = ' ';
    num[9] = '-'; 
    let number = isLoginNumber ? this.loginNumber : this.signupNumber;

    if(number.length > 0){
        let tempNum: string = '';
        for(let i=0; i<number.length; ++i){
            if(/[0-9]/.test(number[i])){
                tempNum += number[i];
            }
        }
        // this.noFormatNumber = tempNum;
        if(isLoginNumber){
          this.rawLoginNumber = tempNum;
        } else {
          this.rawSignupNumber = tempNum;
        }

        for(let j=0; j<tempNum.length; ++j){
            if(j<3){
                num[j+1] = tempNum[j];
            } else if(j<6) {
                num[j+3] = tempNum[j];
            } else {
                num[j+4] = tempNum[j];
            }
        }
        let nullIndex = num.findIndex(i => i == null);
        let len = nullIndex == -1 ? 14 : nullIndex < 7 ? 4 : nullIndex < 11 ? 9 : 14;
        if(nullIndex < 7 && nullIndex != -1){
            num[0] = null;
        }
        let finalNum: string = '';
        for(let i=0; i<len; ++i){
            if(num[i] != null){
                finalNum += num[i];
            }
        }

        if(isLoginNumber){
          this.loginNumber = finalNum;
        } else {
          this.signupNumber = finalNum;
        }
        // number = finalNum;
    }
}

  //Remove style on destroy
  ngOnDestroy(): void {
    document.body.style.removeProperty('overflow')
    this.onDestroy$.next();
  }
}
